import React, { useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { formatDate } from "@transformers/date";
import { GSGrid, Icons, Loader, View } from "@components";
import { ReportActionTypesEnums } from "@modules/reporting/constants/reports";
import actionCreators from "@modules/reporting/actionCreator";
import classNames from "classnames";
import "./index.scss";
import type { State } from "@store/reducers";
import { getReportingParameters } from "@modules/service/selectors/reporting-parameters";
import type { ReportingParametersData } from "@modules/service/reducer/reporting-parameters/types";
import serviceActionCreators from "@modules/service/actionCreator";
import { ScheduleReportSettingsModal } from "@modules/reporting/components";
import {
  isLoading,
  getScheduleReportSettingsModal,
  getScheduleReportSettingsModalData,
} from "@modules/reporting/selectors/modals";
import { getProfile } from "@modules/account/selectors";
import type { Profile } from "@modules/account/types";
import type { ScheduledReportData } from "@modules/reporting/reducer/types";
import { USER_TYPES } from "@modules/account/constants";

type OwnProps = {
  clientId: number,
  reports: Array<Object>,
  runReport: (line: Object) => void,
  deleteReport: (data: { clientId: number, reportId: number, successMessage: string }) => void,
  disabled?: boolean,
};

type PropsFromDispatch = $Exact<{
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
  getScheduledReportData: typeof actionCreators.GetScheduledReportData,
  setScheduledReportSettingsModalData: typeof actionCreators.SetScheduledReportSettingsModalData,
  toggleSaveReportModal: typeof actionCreators.ToggleSaveReportModal,
  toggleScheduleReportSettingsModal: typeof actionCreators.ToggleScheduleReportSettingsModal,
  deleteSavedAbsenceDetailsReport: typeof actionCreators.DeleteSavedAbsenceDetailsReport,
  getReportingParameters: typeof serviceActionCreators.GetReportingParameters,
  submitScheduleReportSettings: typeof actionCreators.SubmitScheduleReportSettings,
}>;
type PropsFromState = $Exact<{
  userProfile: Profile | null,
  reportingParameters: ReportingParametersData,
  scheduleReportSettingsModal: ScheduleReportSettingsModal,
  scheduleReportSettingsModalData: ScheduledReportData | null,
  isLoading: boolean,
}>;

type Props = {
  ...OwnProps,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...PropsFromLocalize,
};

export function MyReportsTable(props: Props) {
  useEffect(() => {
    props.getReportingParameters({ clientId: props.clientId });
  }, []);

  function onToggleActionModal(source: Object) {
    return function() {
      switch (source.type) {
        case ReportActionTypesEnums.COPY:
          return props.toggleSaveReportModal({
            visible: true,
            type: source.type,
            data: {
              modalName: props.t("COPY_REPORT"),
              fieldName: props.t("NEW_REPORT_COPY_NAME"),
              submitButtonName: props.t("COPY"),
              key: `${(source.data || {}).name || ""} Copy`,
            },
            report: source.data,
          });
        case ReportActionTypesEnums.RENAME:
          return props.toggleSaveReportModal({
            visible: true,
            type: source.type,
            data: {
              modalName: props.t("RENAME_REPORT"),
              fieldName: props.t("REPORT_NAME"),
              submitButtonName: props.t("RENAME"),
              key: (source.data || {}).name,
            },
            report: source.data,
          });
        case ReportActionTypesEnums.DELETE:
          return props.deleteReport({
            clientId: props.clientId,
            reportId: source.data?.id,
            successMessage: props.t("REPORT_NAME_WAS_SUCCESSFULLY_REMOVED", {
              name: source.data?.name,
            }),
          });
        default:
          return props.toggleAddEditReportModal(source);
      }
    };
  }

  function onScheduleReportClick(item: Object) {
    const scheduledReportDto = item && item.scheduledReportDto;
    if (scheduledReportDto && props.userProfile) {
      props.getScheduledReportData({
        clientId: props.userProfile.user.clientId,
        employeeId: props.userProfile.user.id,
        scheduledReportId: scheduledReportDto.id,
      });
    } else {
      props.setScheduledReportSettingsModalData({ savedReportId: item.id });
    }

    props.toggleScheduleReportSettingsModal();
  }

  function onScheduleReportSettingsSubmit({ formData }: Object) {
    // need to save scheduled report data against (logged user / employee) clientId
    // and retrieve the my saved report against the selected client
    // that is why pass both clientId's
    props.submitScheduleReportSettings({
      employeeClientId: props.userProfile && props.userProfile.user.clientId,
      employeeId: props.userProfile && props.userProfile.user.id,
      clientId: props.clientId,
      formData,
    });
  }

  function selectedReportName() {
    const { reports, scheduleReportSettingsModalData } = props;

    if (reports && scheduleReportSettingsModalData) {
      const selectedReport = reports.find(
        i => i.id === scheduleReportSettingsModalData.savedReportId,
      );
      return (selectedReport && selectedReport.name) || "";
    }

    return "";
  }

  function columnsConfig() {
    const scheduledReportsEnabled = props.reportingParameters.scheduledReportsEnabled || false;
    const scheduledReportAccess = props.reportingParameters.scheduledReportAccess || [];

    let userType = props.userProfile?.user?.userType || "";
    // FULL_HR_ACCESS is the correct user type name
    // but the list with allowed user used the old naming FULL_ACCESS, LINE_MANAGER
    // when the logged user is FULL_HR_ACCESS, it should check for the old naming
    // this is temporary fix until the BE allow to save the correct naming in scheduled report configuration
    if (userType === USER_TYPES.FULL_HR_ACCESS) {
      userType = "FULL_ACCESS";
    } else if (userType === USER_TYPES.MANAGER) {
      userType = "LINE_MANAGER";
    }

    // the list of allowed user types is defined from the clients configuration "scheduledReportAccess"
    // but includes client's company user types only. Admin and Service Manager should also have access
    // and need to be added to the allowed list
    const _scheduledReportAccess = [...scheduledReportAccess, "ADMIN", "SERVICE_MANAGER"];

    // hide the column
    // when report is enabled
    // and logged user (user type) exists (!== -1) in the allowed list
    const sendScheduledReport: boolean =
      (scheduledReportsEnabled === true &&
        scheduledReportAccess?.length > 0 &&
        scheduledReportAccess.indexOf(userType) !== -1) ||
      _scheduledReportAccess.indexOf(userType) !== -1;

    let cols = [
      {
        key: "id",
        width: 100,
        frozen: "left",
        name: props.t("ID"),
      },
      {
        key: "name",
        align: "left",
        name: props.t("REPORT_NAME"),
      },
      {
        key: "createdAt",
        name: props.t("DATE_CREATED"),
        renderCell: ({ row }: Object) => formatDate(row.createdAt),
      },
      {
        key: "run",
        name: props.t("RUN"),
        align: "center",
        width: 80,
        frozen: "right",
        renderCell: ({ row }: Object) => (
          <span className="material-icons font-size-32 pointer" onClick={props.runReport(row)}>
            play_circle
          </span>
        ),
      },
      {
        key: "edit",
        name: props.t("EDIT"),
        align: "center",
        width: 80,
        frozen: "right",
        contextMenuConfig: (row: Object) =>
          Object.values(ReportActionTypesEnums).map((type, index) => ({
            label: props.t(type),
            onClick: onToggleActionModal({
              visible: true,
              data: row,
              type,
            }),
          })),
      },
    ];
    if (sendScheduledReport) {
      cols.push({
        key: "schedule",
        name: props.t("SCHEDULE"),
        align: "center",
        width: 80,
        frozen: "right",
        onCellClick: (row: Object) => onScheduleReportClick(row),
        renderCell: ({ row }: Object) => {
          const { scheduledReportDto } = row;
          const positive = scheduledReportDto && scheduledReportDto.status;
          const negative = scheduledReportDto && !scheduledReportDto.status;

          let title = "SETUP_REPORT";
          let iconColor = "black";
          if (positive) {
            title = "REPORT_ENABLED";
            iconColor = "positive";
          } else if (negative) {
            title = "REPORT_DISABLED";
            iconColor = "negative";
          }

          return GSGrid.render.materialIcon("event_repeat", iconColor, props.t(title));
        },
      });
    }

    return cols;
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <GSGrid
        columns={columnsConfig()}
        rows={props.reports}
        noResultsText={props.t("NO_REPORTS_MESSAGE")}
      />
      {props.scheduleReportSettingsModal.visible && (
        <ScheduleReportSettingsModal
          userProfile={props.userProfile}
          reportingParameters={props.reportingParameters}
          scheduleReportSettingsModalData={props.scheduleReportSettingsModalData}
          onToggle={props.toggleScheduleReportSettingsModal}
          onSubmit={onScheduleReportSettingsSubmit}
          reportName={selectedReportName()}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  reportingParameters: getReportingParameters(state),
  scheduleReportSettingsModalData: getScheduleReportSettingsModalData(state),
  scheduleReportSettingsModal: getScheduleReportSettingsModal(state),
  isLoading: isLoading(state),
  userProfile: getProfile(state),
});

const mapDispatchToProps = {
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
  getScheduledReportData: actionCreators.GetScheduledReportData,
  setScheduledReportSettingsModalData: actionCreators.SetScheduledReportSettingsModalData,
  toggleSaveReportModal: actionCreators.ToggleSaveReportModal,
  toggleScheduleReportSettingsModal: actionCreators.ToggleScheduleReportSettingsModal,
  getReportingParameters: serviceActionCreators.GetReportingParameters,
  deleteSavedAbsenceDetailsReport: actionCreators.DeleteSavedAbsenceDetailsReport,
  submitScheduleReportSettings: actionCreators.SubmitScheduleReportSettings,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(MyReportsTable);
